import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { api, ApiError } from '../lib/api'
import { Banner, Button, Card, Chip, TextField } from '../ui'

/**
 * Where the customer attaches their own database. Deliberately explicit about who ends up
 * holding the data, because that is the whole reason for doing it this way.
 */
export function TenantSetup({ hideWhenReady = false }: { hideWhenReady?: boolean } = {}) {
  const queryClient = useQueryClient()
  // Neon by default, not Supabase: Supabase's free tier pauses the whole project after 7 days
  // of inactivity and needs a manual restart from its own dashboard, which a trial nobody opens
  // daily will hit silently. Neon's free tier scales to zero and resumes itself on the next
  // request — no dead end, no dashboard to remember. Either is fine to pick deliberately; this
  // is just which one a customer lands on without having read the difference.
  const [provider, setProvider] = useState<'supabase' | 'neon'>('neon')
  const [connectionString, setConnectionString] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [regionId, setRegionId] = useState('aws-eu-central-1')
  // Opts into the Supabase/Neon bring-your-own-database form. Left closed by default whenever a
  // managed database is available, since that's the zero-input path everyone should hit first.
  const [advancedOpen, setAdvancedOpen] = useState(false)

  const tenant = useQuery({ queryKey: ['tenant'], queryFn: api.tenant })

  const provision = useMutation({
    mutationFn: (payload: Parameters<typeof api.provisionTenant>[0]) => api.provisionTenant(payload),
    onSuccess: async () => {
      // Cleared immediately: the credential did its one job and is not needed again.
      setConnectionString('')
      setApiKey('')
      await queryClient.invalidateQueries({ queryKey: ['tenant'] })
      await queryClient.invalidateQueries({ queryKey: ['inbox'] })
    },
  })

  // On the inbox, the connected-status card is pure noise on the page an agent lives in all day —
  // it only earns its space when the database still needs attention. Rendered in full on Settings.
  if (hideWhenReady && tenant.data?.tenant?.status === 'ready') return null
  if (tenant.isPending) return <div className="skeleton" style={{ height: '220px' }} />
  if (tenant.isError) {
    // A fetch failure used to fall straight through to the "Connect your database" form below —
    // on the inbox that meant a transient network blip could make an already-configured
    // customer's daily screen suddenly demand they set up their database again. Staying quiet
    // here (matching the "ready" case above) is safer than guessing; Settings still speaks up,
    // since that's the one place someone is actually looking for this status.
    if (hideWhenReady) return null
    return (
      <Card variant="outlined">
        <p className="cad-muted">
          {tenant.error instanceof ApiError ? tenant.error.message : 'Could not load your database status.'}
        </p>
      </Card>
    )
  }

  const current = tenant.data?.tenant
  const regions = tenant.data?.regions ?? []
  const error = provision.error
  const message = error instanceof ApiError || error instanceof Error ? error.message : undefined

  if (current?.status === 'ready') {
    const region = regions.find((r) => r.id === current.region)
    const ownerLabel =
      current.provider === 'supabase'
        ? 'Your Supabase account'
        : current.ownership === 'customer'
          ? 'Your Neon account'
          : 'Wami'
    return (
      <Card variant="outlined">
        <div className="stack gap-3">
          <div className="row between wrap gap-3">
            <h2 className="cad-section-title">Your database</h2>
            <Chip tone="success">Connected</Chip>
          </div>
          <dl className="kv">
            {current.provider !== 'supabase' && (
              <div>
                <dt>Location</dt>
                <dd>{region?.label ?? current.region ?? 'Unknown'}</dd>
              </div>
            )}
            <div>
              <dt>Owned by</dt>
              <dd>{ownerLabel}</dd>
            </div>
            {current.provider === 'neon' && (
              <div>
                <dt>Project</dt>
                <dd className="tabular">{current.neonProjectId ?? '—'}</dd>
              </div>
            )}
          </dl>
          <p className="cad-muted">
            {current.provider === 'supabase'
              ? 'Conversations, tickets and contacts are written only to this database, inside your own Supabase project. Rotating your database password in Supabase cuts our access immediately.'
              : current.ownership === 'customer'
                ? 'Conversations, tickets and contacts are written only to this database. Revoking the key in Neon cuts our access immediately.'
                : 'Conversations, tickets and contacts are written only to this database, hosted and managed by Wami.'}
          </p>
        </div>
      </Card>
    )
  }

  const managedAvailable = tenant.data?.managedAvailable ?? false

  if (managedAvailable && !advancedOpen) {
    return (
      <Card variant="outlined">
        <div className="stack gap-4">
          <div className="stack gap-2">
            <h2 className="cad-section-title">Set up your database</h2>
            <p className="cad-muted" style={{ maxWidth: '58ch' }}>
              Wami provisions a private Postgres database for you automatically — nothing to
              create or paste, ready in a few seconds. Conversations, tickets and contacts are
              written only there, isolated from every other workspace.
            </p>
          </div>

          {current?.status === 'failed' && current.failureReason && (
            <Banner tone="error">{current.failureReason}</Banner>
          )}
          {message && <Banner tone="error">{message}</Banner>}

          <Button
            size="large"
            onClick={() => provision.mutate({ provider: 'managed' })}
            disabled={provision.isPending}
          >
            {provision.isPending ? 'Setting up your database…' : 'Set up my database'}
          </Button>

          <button
            type="button"
            className="cad-link-button"
            onClick={() => setAdvancedOpen(true)}
            style={{ alignSelf: 'flex-start' }}
          >
            Advanced: bring your own Supabase or Neon database
          </button>
        </div>
      </Card>
    )
  }

  return (
    <Card variant="outlined">
      <form
        className="stack gap-4"
        onSubmit={(event: FormEvent) => {
          event.preventDefault()
          if (provider === 'supabase' ? connectionString.trim() : apiKey.trim()) {
            provision.mutate(
              provider === 'supabase' ? { provider: 'supabase', connectionString } : { provider: 'neon', apiKey, regionId },
            )
          }
        }}
      >
        <div className="stack gap-2">
          <h2 className="cad-section-title">Connect your database</h2>
          {!managedAvailable && (
            <Banner tone="info">
              Wami-managed hosting — the zero-input option — isn't turned on for this deployment
              right now, so bringing your own database is the only path today. If that changes,
              this screen will offer the managed option first.
            </Banner>
          )}
          <p className="cad-muted" style={{ maxWidth: '58ch' }}>
            Your conversations live in a Postgres database in your own account, not in a shared
            table with other customers — full data sovereignty, and nothing of yours ever sits on
            our servers.
          </p>
          {managedAvailable && (
            <button
              type="button"
              className="cad-link-button"
              onClick={() => setAdvancedOpen(false)}
              style={{ alignSelf: 'flex-start' }}
            >
              ← Use a Wami-managed database instead
            </button>
          )}
        </div>

        <div className="row gap-2">
          <button
            type="button"
            className="selectable"
            data-selected={provider === 'supabase'}
            onClick={() => setProvider('supabase')}
          >
            Supabase
          </button>
          <button type="button" className="selectable" data-selected={provider === 'neon'} onClick={() => setProvider('neon')}>
            Neon
          </button>
        </div>

        {provider === 'supabase' && (
          <p className="cad-muted" style={{ maxWidth: '58ch' }}>
            Free-tier Supabase projects pause after 7 days with no activity and need a manual
            restart from your Supabase dashboard — easy to miss on a trial you don't open every
            day. Neon's free tier has no such pause; it scales to zero and wakes itself on the
            next message.
          </p>
        )}

        {provider === 'supabase' ? (
          <>
            <p className="cad-muted" style={{ maxWidth: '58ch' }}>
              Create a free project at{' '}
              <a href="https://supabase.com/dashboard/new" target="_blank" rel="noreferrer">
                supabase.com
              </a>{' '}
              (about two minutes, no card required), then paste its connection string below —
              Project Settings → Database → Connection string. We use it once to verify it works,
              then store it encrypted; we never see your Supabase account itself.
            </p>
            <TextField
              label="Supabase connection string"
              value={connectionString}
              onChange={(event) => setConnectionString(event.target.value)}
              type="password"
              autoComplete="off"
              placeholder="postgresql://postgres:…@db.….supabase.co:5432/postgres"
              help="Project Settings → Database → Connection string, in your Supabase dashboard."
            />
          </>
        ) : (
          <>
            <p className="cad-muted" style={{ maxWidth: '58ch' }}>
              Create a key in Neon and we will provision one project with it — the key is used
              once and never stored.
            </p>
            <div className="stack gap-2">
              <span className="cad-label">Where should your data live?</span>
              <div className="stack gap-2">
                {regions.map((region) => (
                  <button
                    key={region.id}
                    type="button"
                    className="selectable"
                    data-selected={regionId === region.id}
                    onClick={() => setRegionId(region.id)}
                    style={{ textAlign: 'left' }}
                  >
                    <div className="stack gap-1">
                      <span className="cad-label">{region.label}</span>
                      <span className="cad-muted">{region.hint}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <TextField
              label="Neon API key"
              value={apiKey}
              onChange={(event) => setApiKey(event.target.value)}
              type="password"
              autoComplete="off"
              placeholder="napi_…"
              help="Neon console → Account settings → API keys. Used once to create the project."
            />
          </>
        )}

        {current?.status === 'failed' && current.failureReason && (
          <Banner tone="error">{current.failureReason}</Banner>
        )}
        {message && <Banner tone="error">{message}</Banner>}

        <Button
          type="submit"
          size="large"
          disabled={(provider === 'supabase' ? !connectionString.trim() : !apiKey.trim()) || provision.isPending}
        >
          {provision.isPending
            ? provider === 'supabase'
              ? 'Connecting your database…'
              : 'Creating your database…'
            : provider === 'supabase'
              ? 'Connect my database'
              : 'Create my database'}
        </Button>

        {provider === 'neon' && tenant.data?.oauthAvailable === false && (
          <p className="cad-muted">
            One-click sign-in with Neon becomes available once this deployment is registered as a
            Neon OAuth application. Until then, pasting a key does the same thing.
          </p>
        )}
      </form>
    </Card>
  )
}
